import { useEffect, useState } from "react"
import {
  useAccount,
  useVoidAndReissueCheckMutation,
} from "@apis/accounts/useAccounts"
import { useGetAddressQuery } from "@apis/address/useAddress"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { AxiosError } from "axios"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import { Button } from "@components/ui/button"
import { AddressInfoForm } from "@components/Address/AddressInfoForm"
import { Loader2 } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import { accountKeys } from "@apis/accounts/queryKeys"

type TReissueInformationFormProps = {
  account_id: string
  account_payable_bill_id: number
  address_guid: string
  onSubmitSuccess?: () => void
}

type TFormValues = {
  first_name: string
  last_name: string
}

export const ReissueInformationForm = ({
  account_id,
  account_payable_bill_id,
  address_guid,
  onSubmitSuccess,
}: TReissueInformationFormProps) => {
  const queryClient = useQueryClient()
  const getAccountQuery = useAccount(account_id)
  const getAddressQuery = useGetAddressQuery(getAccountQuery.data?.address_guid)
  const setVoidAndReissueCheckMutation = useVoidAndReissueCheckMutation()

  const [showAddressForm, setShowAddressForm] = useState(false)

  const form = useForm<TFormValues>({
    mode: "onTouched",
    defaultValues: {
      first_name: "",
      last_name: "",
    },
  })

  useEffect(() => {
    if (getAccountQuery.data) {
      form.setValue(
        "first_name",
        getAccountQuery.data.membership_profile.first_name,
        {
          shouldValidate: true,
        },
      )
      form.setValue(
        "last_name",
        getAccountQuery.data.membership_profile.last_name,
        {
          shouldValidate: true,
        },
      )
    }
  }, [getAccountQuery.data, form])

  const handleVoidAndReissue = (formData: TFormValues) => {
    setVoidAndReissueCheckMutation.mutate(
      {
        account_id,
        account_payable_bill_id,
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          address_guid,
        },
      },
      {
        onSuccess: () => {
          toast.success("Check successfully Reissued!")
          onSubmitSuccess?.()
        },
        onError: (error: string | AxiosError) =>
          toast.error(`Error reissuing check! ${error}`),
      },
    )
  }

  const handleSaveAddress = () => {
    queryClient.invalidateQueries({
      queryKey: accountKeys.detail(account_id),
    })

    setShowAddressForm(false)
  }

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="tw:space-y-6">
        <div className="tw:grid tw:grid-cols-2 tw:gap-4">
          <div className="tw:space-y-4">
            <FormField
              control={form.control}
              name="first_name"
              rules={{ required: "First name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              rules={{ required: "Last name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="tw:flex tw:flex-col tw:justify-between tw:gap-4 tw:rounded tw:border tw:border-gray-200 tw:p-4">
            {showAddressForm ? (
              <AddressInfoForm
                identifier={account_id}
                address_type_code="service"
                onSave={() => handleSaveAddress()}
                onCancel={() => setShowAddressForm(false)}
              />
            ) : (
              <>
                {getAddressQuery.isFetching || getAccountQuery.isFetching ? (
                  <Loader2 className="tw:animate-spin" />
                ) : (
                  getAddressQuery.data && (
                    <>
                      <div className="tw:space-y-1">
                        <p className="tw:font-medium">
                          {`${getAddressQuery.data.street ?? ""} ${getAddressQuery.data.unit ?? ""}`}
                        </p>
                        <p className="tw:font-medium">
                          {`${getAddressQuery.data.city ?? ""}, ${getAddressQuery.data.state_code ?? ""} ${getAddressQuery.data.zip ?? ""}`}
                        </p>
                      </div>
                      <div className="tw:text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowAddressForm(true)}
                        >
                          Edit Address
                        </Button>
                      </div>
                    </>
                  )
                )}
              </>
            )}
          </div>
        </div>

        <div className="tw:flex tw:justify-end">
          <Button
            type="button"
            variant="destructive"
            disabled={
              !form.formState.isValid ||
              getAccountQuery.isFetching ||
              getAddressQuery.isFetching ||
              setVoidAndReissueCheckMutation.isPending ||
              showAddressForm
            }
            onClick={form.handleSubmit(handleVoidAndReissue)}
          >
            {setVoidAndReissueCheckMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>Void and Reissue Check</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
